import { z } from 'zod';
import { NOTE_FORMATS, appointmentKeySchema, progressBodySchema } from './clinical-notes.js';
/**
 * calendar#46 step 7 - group notes (PROGRESS_NOTE_GROUP_A-3). A group note session is
 * one calendar group appointment occurrence; each attendee gets an ordinary progress
 * note linked to it. The roster comes from the calendar (enrolled, checked in, checked
 * out); the session holds the one template and the shared text.
 */
/** Open (find or create) the session for a calendar group appointment occurrence. */
export const groupNoteOpenSchema = z.object({ appointmentKey: appointmentKeySchema });
/** Session setup: the template for every note, and the shared text. */
export const groupNoteUpdateSchema = z
    .object({
    format: z.enum(NOTE_FORMATS).optional(),
    groupSummary: z.string().max(20000).optional(),
    sessionSummary: z.string().max(40000).optional(),
    approaches: z.array(z.string().max(120)).max(30).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Provide at least one field to update.' });
/** ehr#437 (GROUP_NOTE V26, 31 A): walk-ins, from the practice's contacts, each with their own note. */
export const groupWalkInSchema = z
    .object({
    add: z.array(z.string().uuid()).max(10).optional(),
    remove: z.array(z.string().uuid()).max(10).optional(),
})
    .refine((v) => (v.add?.length ?? 0) + (v.remove?.length ?? 0) > 0, {
    message: 'Add or remove at least one walk-in.',
});
/** Save one attendee's note (created on first save). */
export const groupAttendeeNoteSchema = z.object({ body: progressBodySchema });
/** Submit all (or the listed attendees') notes for review. */
export const groupSubmitSchema = z.object({
    contactIds: z.array(z.string().uuid()).max(100).optional(),
    cosignerId: z.string().uuid().optional(),
});
/**
 * ehr#688 (GROUP_NOTE_10-29-V25 "Co-sign all ready notes"): the reviewer co-signs every note of the
 * session that is pending review (or only the listed attendees'), each through the normal co-sign.
 */
export const groupCosignSchema = z.object({
    contactIds: z.array(z.string().uuid()).max(100).optional(),
});
/** Calendar roster statuses that count as attending (the rail shows these). */
export const GROUP_ROSTER_STATUSES = ['enrolled', 'checked-in', 'checked-out'];
/**
 * Roster names that appear in a group summary. The summary is copied into every
 * attendee's note, so a name there puts one client in another client's record. Each
 * word of a name (3+ letters) is matched as a whole word, case-insensitive; returns
 * the full names found. Used by the API (nameFlags) and the workspace (live warning).
 */
export function rosterNameFlags(summary, names) {
    const text = summary.toLowerCase();
    if (!text.trim())
        return [];
    const hit = (w) => {
        const t = w.trim().toLowerCase();
        if (t.length < 3)
            return false;
        const esc = t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`(^|[^\\p{L}\\p{N}])${esc}($|[^\\p{L}\\p{N}])`, 'u').test(text);
    };
    return names.filter((n) => n.split(/\s+/).some(hit));
}
//# sourceMappingURL=group-notes.js.map